import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  Position,
  Edge,
} from "@xyflow/react";
import { useEffect, useState } from "react";

interface FloatingAnimatedEdgeData extends Record<string, unknown> {
  color?: string;
  animated?: boolean;
  pattern?: "dashed" | "dotted" | "solid";
  direction?: "forward" | "reverse";
}

export const FloatingAnimatedEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data = {} as FloatingAnimatedEdgeData,
  style = {},
  markerEnd,
}: EdgeProps<Edge<FloatingAnimatedEdgeData>>) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const [animationProgress, setAnimationProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (data.animated) {
      const animation = setInterval(() => {
        setAnimationProgress((prev) => (prev + 1) % 100);
      }, 50);
      return () => clearInterval(animation);
    }
  }, [data.animated]);

  const getPatternStyle = () => {
    switch (data.pattern) {
      case "dashed":
        return { strokeDasharray: "5,5" };
      case "dotted":
        return { strokeDasharray: "2,2" };
      default:
        return {};
    }
  };

  const getAnimationStyle = () => {
    if (!data.animated) return {};
    
    const direction = data.direction === "reverse" ? -1 : 1;
    const offset = (animationProgress * direction) % 100;
    
    return {
      strokeDasharray: "5,5",
      strokeDashoffset: offset,
      transition: "stroke-dashoffset 0.05s linear",
    };
  };

  const getColor = () => {
    if (isHovered) return "#ff0072";
    return data.color || "#b1b1b7";
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          ...getPatternStyle(),
          ...getAnimationStyle(),
          stroke: getColor(),
          strokeWidth: isHovered ? 2 : 1,
          transition: "stroke 0.3s, stroke-width 0.3s",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
    </>
  );
}; 